'use client';

import { fetchMarketingMainFlashSaleProductList } from '@/api/marketingPages';
import { useJump } from '@/components/MagicJump';
import Image from '@/components/Image';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { useLocale } from 'next-intl';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useInView } from 'react-intersection-observer';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { MarketingFlashSaleBlockConfig } from '../../../types';
import {
  isMarketingFlashSaleProductSoldOut,
  resolveMarketingFlashSaleBlock,
  selectMarketingFlashSaleProducts,
  shouldShowMarketingFlashSaleCountdown,
  shouldRefreshMarketingFlashSaleForToken,
} from '../flashSale';
import {
  getMarketingFlashSaleDisplayPrice,
  getMarketingFlashSaleProductHref,
  getMarketingFlashSaleProductImage,
  getMarketingFlashSaleProductName,
  getStringValue,
  type MarketingFlashSaleProductItem,
  withS3Url,
} from './marketingAnniversaryUtils';
import { MarketingSwiperArrowButton } from './MarketingSwiperArrowButton';

const MARKETING_FLASH_SALE_PC_SPACE_BETWEEN = 16;
const MARKETING_FLASH_SALE_PC_CARD_WIDTH =
  'clamp(208px, min(calc(25vw - 48px), calc(18.125vw + 40px)), 388px)';
const MARKETING_FLASH_SALE_PC_CARD_HEIGHT =
  'clamp(200px, min(calc(13.28125vw + 64px), calc(8.75vw + 122px)), 290px)';
const MARKETING_FLASH_SALE_PC_CARD_PADDING_BOTTOM =
  'clamp(16px, min(calc(0.78125vw + 8px), calc(0.9375vw + 6px)), 24px)';
const MARKETING_FLASH_SALE_PC_IMAGE_MAX_WIDTH = '308px';
const MARKETING_FLASH_SALE_PC_IMAGE_ASPECT_RATIO = '2 / 1';
const MARKETING_FLASH_SALE_PC_CONTENT_MARGIN_TOP =
  'clamp(6px, min(calc(0.390625vw + 2px), calc(0.15625vw + 5px)), 8px)';
const MARKETING_FLASH_SALE_PC_BUTTON_MARGIN_TOP =
  'clamp(8px, min(calc(1.5625vw - 8px), calc(1.25vw - 4px)), 16px)';
const MARKETING_FLASH_SALE_PC_BUTTON_WIDTH = '180px';
const MARKETING_FLASH_SALE_PC_BUTTON_HEIGHT =
  'clamp(34px, min(calc(0.78125vw + 26px), calc(0.625vw + 28px)), 40px)';
const MARKETING_FLASH_SALE_PC_BUTTON_ACTION_WIDTH = 'clamp(58px, calc(1.5625vw + 42px), 72px)';
const MARKETING_FLASH_SALE_SMALL_LANGUAGE_LOCALES = ['de', 'fr'];
const MARKETING_FLASH_SALE_PC_SWIPER_SLIDE_STYLE: CSSProperties = {
  marginRight: `${MARKETING_FLASH_SALE_PC_SPACE_BETWEEN}px`,
  width: MARKETING_FLASH_SALE_PC_CARD_WIDTH,
};

interface MarketingFlashSaleSectionProps {
  flashSaleBlock: MarketingFlashSaleBlockConfig;
  marketingPageId: number;
  isPreview: boolean;
  adp?: string;
  isWap: boolean;
  onProductExposure?: (product: MarketingFlashSaleProductItem, index: number) => void;
  onProductClick?: (product: MarketingFlashSaleProductItem, index: number) => void;
}

interface MarketingFlashSaleProductCardProps {
  product: MarketingFlashSaleProductItem;
  index: number;
  buttonText: string;
  buttonColor: string;
  buttonImage?: string;
  isWap: boolean;
  isSoldOut: boolean;
  onProductExposure?: (product: MarketingFlashSaleProductItem, index: number) => void;
  onProductClick?: (product: MarketingFlashSaleProductItem, index: number) => void;
}

function getMarketingFlashSaleRemainingSeconds(
  countdownEndAt?: string | null,
  remainingSeconds?: number | null,
) {
  if (remainingSeconds && remainingSeconds > 0) {
    return remainingSeconds;
  }

  if (!countdownEndAt) {
    return 0;
  }

  const endTime = new Date(countdownEndAt).getTime();
  if (!Number.isFinite(endTime)) {
    return 0;
  }

  return Math.max(0, Math.floor((endTime - Date.now()) / 1000));
}

function formatCountdownPart(value: number) {
  return String(Math.max(0, value)).padStart(2, '0');
}

function useMarketingFlashSaleCountdown(
  countdownEndAt?: string | null,
  remainingSeconds?: number | null,
) {
  const initialSeconds = useMemo(
    () => getMarketingFlashSaleRemainingSeconds(countdownEndAt, remainingSeconds),
    [countdownEndAt, remainingSeconds],
  );
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);

  useEffect(() => {
    setSecondsLeft(initialSeconds);

    if (initialSeconds <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setSecondsLeft((current) => Math.max(0, current - 1));
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [initialSeconds]);

  const hours = Math.floor(secondsLeft / 3600);
  const minutes = Math.floor((secondsLeft % 3600) / 60);
  const seconds = secondsLeft % 60;

  return [hours, minutes, seconds] as const;
}

function MarketingFlashSaleProductCard({
  product,
  index,
  buttonText,
  buttonColor,
  buttonImage,
  isWap,
  isSoldOut,
  onProductExposure,
  onProductClick,
}: MarketingFlashSaleProductCardProps) {
  const tMarketing = useTranslations('activity.campaigns.marketing');
  const locale = useLocale();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const toJumpTarget = useJump();
  const image = getMarketingFlashSaleProductImage(product);
  const name = getMarketingFlashSaleProductName(product);
  const href = getMarketingFlashSaleProductHref(product);
  const { sellPrice } = getMarketingFlashSaleDisplayPrice(product, currency, userInfo);
  const formattedSellPrice = sellPrice ? formatPriceNumberWithSymbol(sellPrice, currency) : '';
  const isSmallLanguage = MARKETING_FLASH_SALE_SMALL_LANGUAGE_LOCALES.some((language) =>
    locale.toLowerCase().startsWith(language),
  );
  const { ref: exposureRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const exposureSentRef = useRef(false);

  useEffect(() => {
    if (!inView || exposureSentRef.current) {
      return;
    }

    exposureSentRef.current = true;
    onProductExposure?.(product, index);
  }, [inView, index, onProductExposure, product]);

  const handleProductClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onProductClick?.(product, index);

    if (
      !href ||
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    toJumpTarget({
      jump_type: 4,
      Jump_target: href,
    });
  };

  if (isWap) {
    return (
      <a
        ref={exposureRef}
        href={href || undefined}
        className="relative box-border flex h-[136px] w-[126px] shrink-0 flex-col items-center gap-[8px] overflow-hidden rounded-[8px] bg-white px-[10px] pb-[10px] pt-[16px] text-center"
        aria-label={name}
        onClick={handleProductClick}
      >
        <div className="relative h-[52px] w-[104px] shrink-0">
          {image && <Image src={image} alt={name} fill sizes="104px" className="object-cover" />}
          {isSoldOut && (
            <span className="pointer-events-none absolute left-1/2 top-1/2 z-[2] flex size-[52px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-center">
              <span className="whitespace-pre-wrap text-[10px] leading-[12px] text-white">
                {tMarketing('flashSale.soldOut')}
              </span>
            </span>
          )}
        </div>
        <div className="w-full truncate font-br text-[12px] font-normal leading-[14px] text-[#151618]">
          {name}
        </div>
        <div
          className="flex h-[28px] w-[106px] overflow-hidden rounded-[6px] font-bb text-white"
          style={{ backgroundColor: buttonColor || '#463AE8' }}
        >
          <span className="flex min-w-0 flex-1 items-center truncate pl-[12px] text-left text-[12px] leading-[14px]">
            {formattedSellPrice}
          </span>
          <span
            className={`flex h-full w-[46px] shrink-0 items-center justify-end pr-[4px] text-right leading-[16px] ${
              isSmallLanguage ? 'text-[11px]' : 'text-[13px]'
            }`}
            style={{
              backgroundImage: buttonImage ? `url(${buttonImage})` : undefined,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% 100%',
            }}
          >
            {buttonText}
          </span>
        </div>

        {isSoldOut && (
          <div className="pointer-events-none absolute inset-0 z-[1] rounded-[8px] bg-white/40" />
        )}
      </a>
    );
  }

  const content = (
    <a
      ref={exposureRef}
      href={href || undefined}
      className="relative box-border flex w-full flex-col items-center overflow-hidden rounded-[12px] bg-white px-[24px] pt-[24px] text-left transition-opacity min-[1025px]:px-[40px] min-[1025px]:pt-[32px]"
      style={{
        height: MARKETING_FLASH_SALE_PC_CARD_HEIGHT,
        paddingBottom: MARKETING_FLASH_SALE_PC_CARD_PADDING_BOTTOM,
      }}
      aria-label={name}
      onClick={handleProductClick}
    >
      <div
        className="relative shrink-0"
        style={{
          width: '100%',
          maxWidth: MARKETING_FLASH_SALE_PC_IMAGE_MAX_WIDTH,
          aspectRatio: MARKETING_FLASH_SALE_PC_IMAGE_ASPECT_RATIO,
        }}
      >
        {image && (
          <Image
            src={image}
            alt={name}
            fill
            sizes={MARKETING_FLASH_SALE_PC_IMAGE_MAX_WIDTH}
            className="object-contain"
          />
        )}
        {isSoldOut && (
          <span className="pointer-events-none absolute left-1/2 top-1/2 z-[2] flex aspect-square h-[5.729vw] max-h-[110px] w-[5.729vw] max-w-[110px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-center">
            <span className="whitespace-pre-wrap text-[clamp(12px,0.885vw,17px)] leading-[clamp(14px,1.042vw,20px)] text-white">
              {tMarketing('flashSale.soldOut')}
            </span>
          </span>
        )}
      </div>
      <div
        className="flex min-h-0 w-full flex-1 flex-col items-center justify-end text-center"
        style={{ marginTop: MARKETING_FLASH_SALE_PC_CONTENT_MARGIN_TOP }}
      >
        <div className="w-full truncate text-center font-br text-[14px] font-normal leading-[17px] text-[#151618]">
          {name}
        </div>
        <div
          className="flex overflow-hidden rounded-[12px] font-bb text-white"
          style={{
            marginTop: MARKETING_FLASH_SALE_PC_BUTTON_MARGIN_TOP,
            width: MARKETING_FLASH_SALE_PC_BUTTON_WIDTH,
            height: MARKETING_FLASH_SALE_PC_BUTTON_HEIGHT,
            backgroundColor: buttonColor || '#463AE8',
          }}
        >
          <span className="flex min-w-0 flex-1 items-center whitespace-nowrap pl-[12px] text-[16px] leading-[19px]">
            {formattedSellPrice}
          </span>
          <span
            className={`relative -ml-[1px] flex shrink-0 items-center justify-end pr-[10px] leading-[19px] ${
              isSmallLanguage ? 'text-[12px]' : 'text-[16px]'
            }`}
            style={{
              width: MARKETING_FLASH_SALE_PC_BUTTON_ACTION_WIDTH,
              backgroundImage: buttonImage ? `url(${buttonImage})` : undefined,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% 100%',
            }}
          >
            <span className="relative z-[1]">{buttonText}</span>
          </span>
        </div>
      </div>

      {isSoldOut && (
        <div className="pointer-events-none absolute inset-0 z-[1] rounded-[8px] bg-white/40 lg:rounded-[10px] 2xl:rounded-[12px]" />
      )}
    </a>
  );

  return content;
}

export function MarketingFlashSaleSection({
  flashSaleBlock,
  marketingPageId,
  isPreview,
  adp,
  isWap,
  onProductExposure,
  onProductClick,
}: MarketingFlashSaleSectionProps) {
  const tMarketing = useTranslations('activity.campaigns.marketing');
  const token = useStore((state) => state.token);
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  const swiperRef = useRef<SwiperType | null>(null);
  const previousTokenRef = useRef<string | undefined>(undefined);
  const latestRequestIdRef = useRef(0);
  const [activeFlashSaleBlock, setActiveFlashSaleBlock] = useState(flashSaleBlock);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  useEffect(() => {
    if (!clientTokenReady || !marketingPageId) {
      return;
    }

    const previousToken = previousTokenRef.current;
    const shouldRefresh = shouldRefreshMarketingFlashSaleForToken(previousToken, token);
    previousTokenRef.current = token;

    if (!shouldRefresh) {
      return;
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    fetchMarketingMainFlashSaleProductList(marketingPageId, isPreview, adp)
      .then((response) => {
        if (latestRequestIdRef.current !== requestId) {
          return;
        }

        setActiveFlashSaleBlock(resolveMarketingFlashSaleBlock(flashSaleBlock, response));
      })
      .catch((error) => {
        if (latestRequestIdRef.current === requestId) {
          logger.error(error, 'Failed to refresh marketing flash sale products:');
        }
      });

    return () => {
      if (latestRequestIdRef.current === requestId) {
        latestRequestIdRef.current += 1;
      }
    };
  }, [adp, clientTokenReady, flashSaleBlock, isPreview, marketingPageId, token]);

  const [hours, minutes, seconds] = useMarketingFlashSaleCountdown(
    activeFlashSaleBlock.countdown_end_at,
    activeFlashSaleBlock.remaining_seconds,
  );
  const bgImage = withS3Url(
    isWap
      ? activeFlashSaleBlock.bg_wap_image || activeFlashSaleBlock.bg_pc_image
      : activeFlashSaleBlock.bg_pc_image || activeFlashSaleBlock.bg_wap_image,
  );
  const buttonImage = withS3Url(
    isWap
      ? activeFlashSaleBlock.button_wap_image || activeFlashSaleBlock.button_pc_image
      : activeFlashSaleBlock.button_pc_image || activeFlashSaleBlock.button_wap_image,
  );
  const products = useMemo(() => {
    const sourceProducts = activeFlashSaleBlock.products as MarketingFlashSaleProductItem[];
    return selectMarketingFlashSaleProducts(
      sourceProducts,
      activeFlashSaleBlock.display_count,
      activeFlashSaleBlock.sold_out_display,
      activeFlashSaleBlock.sold_out_sort,
    ) as MarketingFlashSaleProductItem[];
  }, [
    activeFlashSaleBlock.display_count,
    activeFlashSaleBlock.products,
    activeFlashSaleBlock.sold_out_display,
    activeFlashSaleBlock.sold_out_sort,
  ]);
  const countdownParts = [hours, minutes, seconds];
  const shouldShowCountdown = shouldShowMarketingFlashSaleCountdown(activeFlashSaleBlock);
  const sectionRadius = isWap ? '8px' : '12px';
  const sectionTitle = activeFlashSaleBlock.title || '';
  const sectionSubTitle = activeFlashSaleBlock.sub_title || '';
  const countdownLabel = tMarketing('flashSale.endsIn');
  const productButtonText = activeFlashSaleBlock.button_text || '';

  const syncSwiperEdgeState = useCallback((swiper: SwiperType) => {
    setIsBeginning((current) => (current === swiper.isBeginning ? current : swiper.isBeginning));
    setIsEnd((current) => (current === swiper.isEnd ? current : swiper.isEnd));
  }, []);

  const renderProductCard = (product: MarketingFlashSaleProductItem, index: number) => (
    <MarketingFlashSaleProductCard
      key={`${getStringValue(product.shop_sku) || getStringValue(product.id) || index}`}
      product={product}
      index={index}
      buttonText={productButtonText}
      buttonColor={activeFlashSaleBlock.button_color}
      buttonImage={buttonImage}
      isWap={isWap}
      isSoldOut={isMarketingFlashSaleProductSoldOut(product)}
      onProductExposure={onProductExposure}
      onProductClick={onProductClick}
    />
  );

  const renderCountdown = () => {
    if (!shouldShowCountdown) {
      return null;
    }

    return (
      <div
        className={`flex flex-none items-center text-[#282828] ${
          isWap ? 'gap-[6px]' : 'gap-[10px]'
        }`}
      >
        <span
          className={`font-br font-normal ${
            isWap ? 'text-[12px] leading-[14px]' : 'text-[14px] leading-[17px]'
          }`}
        >
          {countdownLabel}
        </span>
        <div className={isWap ? 'flex items-center gap-[2px]' : 'flex items-center gap-[3px]'}>
          {countdownParts.map((part, index) => (
            <span
              key={index}
              className={isWap ? 'flex items-center gap-[3px]' : 'flex items-center gap-[5px]'}
            >
              <span
                className={`flex items-center justify-center rounded-[6px] bg-primary-black font-bs font-semibold text-white ${
                  isWap
                    ? 'h-[24px] min-w-[24px] px-[4px] text-[12px] leading-[14px]'
                    : 'size-[32px] text-[16px] leading-[19px]'
                }`}
              >
                {formatCountdownPart(part)}
              </span>
              {index < countdownParts.length - 1 && (
                <span
                  className={`font-bs font-semibold text-[#282828] ${
                    isWap ? 'text-[12px] leading-[14px]' : 'text-[16px] leading-[19px]'
                  }`}
                >
                  :
                </span>
              )}
            </span>
          ))}
        </div>
      </div>
    );
  };

  if (!activeFlashSaleBlock.visible) {
    return null;
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <section
      className={
        isWap
          ? 'relative z-10 mx-[8px] my-[20px]'
          : 'relative z-10 mx-auto my-[20px] w-[calc(100%_-_96px)] max-w-[1680px] min-[1281px]:w-[calc(100%_-_240px)]'
      }
    >
      <div
        className={`overflow-hidden bg-cover bg-center ${
          isWap
            ? 'rounded-[8px] px-[12px] pb-[8px] pt-[12px]'
            : 'rounded-[12px] px-[24px] pb-[23px] pt-[32px] min-[1281px]:px-[40px]'
        }`}
        style={{
          backgroundColor: activeFlashSaleBlock.background_color,
          backgroundImage: bgImage ? `url(${bgImage})` : undefined,
          backgroundPosition: 'top center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '100% auto',
          borderRadius: sectionRadius,
          clipPath: `inset(0 round ${sectionRadius})`,
        }}
      >
        <div
          className={`flex ${
            isWap
              ? 'mb-[12px] flex-col items-stretch gap-[2px]'
              : 'mb-[24px] items-center justify-between gap-[8px]'
          }`}
        >
          {isWap ? (
            <>
              <div className="flex w-full items-center justify-between gap-[8px]">
                <h2 className="min-w-0 flex-1 truncate font-bb text-[18px] font-bold leading-[22px] text-[#282828]">
                  {sectionTitle}
                </h2>
                {renderCountdown()}
              </div>
              {sectionSubTitle && (
                <p className="w-full break-words font-br text-[12px] font-normal leading-[14px] text-[#808080]">
                  {sectionSubTitle}
                </p>
              )}
            </>
          ) : (
            <>
              <div className="flex min-w-0 items-center gap-[10px]">
                <h2 className="truncate font-bb text-[24px] font-bold leading-[29px] text-[#282828]">
                  {sectionTitle}
                </h2>
                {sectionSubTitle && (
                  <p className="truncate font-br text-[14px] font-normal leading-[17px] text-[#808080]">
                    {sectionSubTitle}
                  </p>
                )}
              </div>
              {renderCountdown()}
            </>
          )}
        </div>

        {isWap ? (
          <div className="scrollbar-hide -ml-[12px] flex w-[calc(100%_+_24px)] flex-nowrap gap-[6px] overflow-x-auto overscroll-x-contain px-[12px] [-webkit-overflow-scrolling:touch]">
            {products.map((product, index) => renderProductCard(product, index))}
          </div>
        ) : (
          <div className="group relative -ml-[24px] w-[calc(100%_+_48px)] min-[1281px]:-ml-[40px] min-[1281px]:w-[calc(100%_+_80px)]">
            <Swiper
              modules={[Navigation]}
              slidesPerView="auto"
              slidesPerGroup={1}
              spaceBetween={MARKETING_FLASH_SALE_PC_SPACE_BETWEEN}
              slidesOffsetBefore={24}
              slidesOffsetAfter={24}
              breakpoints={{
                1281: {
                  slidesOffsetBefore: 40,
                  slidesOffsetAfter: 40,
                },
              }}
              navigation={{
                nextEl: '.marketing-flash-sale-next',
                prevEl: '.marketing-flash-sale-prev',
              }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                syncSwiperEdgeState(swiper);
              }}
              onSlideChange={syncSwiperEdgeState}
              onProgress={syncSwiperEdgeState}
              onReachBeginning={syncSwiperEdgeState}
              onReachEnd={syncSwiperEdgeState}
              onFromEdge={syncSwiperEdgeState}
              onTransitionEnd={syncSwiperEdgeState}
              className="!overflow-hidden"
            >
              {products.map((product, index) => (
                <SwiperSlide
                  key={`${getStringValue(product.id) || getStringValue(product.shop_sku) || index}`}
                  style={MARKETING_FLASH_SALE_PC_SWIPER_SLIDE_STYLE}
                >
                  {renderProductCard(product, index)}
                </SwiperSlide>
              ))}
            </Swiper>

            <MarketingSwiperArrowButton
              className="marketing-flash-sale-prev left-[8px]"
              direction="prev"
              hidden={isBeginning}
              label="Previous"
              onClick={() => swiperRef.current?.slidePrev()}
            />
            <MarketingSwiperArrowButton
              className="marketing-flash-sale-next right-[8px]"
              direction="next"
              hidden={isEnd}
              label="Next"
              onClick={() => swiperRef.current?.slideNext()}
            />
          </div>
        )}
      </div>
    </section>
  );
}
